/** Where anyone lives: the fields that make placement uneven.
 *
 * A generated map gives itself away by its spacing. Scatter blocking scenery
 * with one probability per cell and the result is a Poisson process - every
 * part of the map equally busy, no thicket, no clearing, no edge to a wood -
 * and an art director reads that instantly as "no landform". The classic's maps
 * are the opposite: a wall of conifers down one side, an open meadow with a
 * single hero standing in it, three woodpiles together by the windmill.
 *
 * Nothing here decides WHAT goes down; it decides how much of it goes where.
 * Two long-wavelength fields do the work, both from `relief` so a step of one
 * cell barely changes them and a wood therefore has a shape:
 *
 *   `wildness`  scrub, trees, boulders - the closed ground.
 *   `crowding`  how much a placement pass should want this cell at all.
 *
 * They are salted apart, so the densest scrub is not automatically where the
 * mines are, and a clearing is as likely to be busy as empty.
 */

import { NOISE_ONE, relief } from "./noise.js";

const WILD_SALT = 0x3c9f1b57;
const CROWD_SALT = 0x7ab41d29;

/** 0..NOISE_ONE. High is closed ground, low is a clearing. */
export const wildness = (x: number, y: number): number => relief(x, y, WILD_SALT);

/** 0..NOISE_ONE. High is where a zone's objects should bunch up. */
export const crowding = (x: number, y: number): number => relief(x, y, CROWD_SALT);

/**
 * Thresholds, not a gradient, for the same reason the road cost field uses one.
 *
 * A multiplier that varies smoothly over the whole range leaves every part of
 * the map somewhat wooded and nowhere either bare or solid; what reads as
 * landform is a boundary. Roughly a fifth of the map is above `THICKET` and
 * about a third below `CLEARING`, in blobs `LONG_PERIOD` cells across.
 */
const THICKET = Math.trunc((NOISE_ONE * 62) / 100);
const CLEARING = Math.trunc((NOISE_ONE * 46) / 100);

/** Per-mille multiplier on the blocking-scenery chance for one cell. */
export function thicketScale(x: number, y: number): number {
  const w = wildness(x, y);
  if (w >= THICKET) return 4600;
  if (w <= CLEARING) return 110;
  // Between the two the wood thins out rather than stopping at a line, so the
  // edge of a thicket is ragged at the scale of the short noise octave.
  const span = THICKET - CLEARING;
  return 110 + Math.trunc(((w - CLEARING) * (1000 - 110)) / (span < 1 ? 1 : span));
}

/**
 * The same field applied to non-blocking decals, but flattened.
 *
 * Decals are ground texture, and ground with no texture at all reads as a bald
 * patch rather than as a clearing - the clearing has to be made of something.
 * So a clearing keeps half its decals where it keeps a tenth of its trees.
 */
export function decalScale(x: number, y: number): number {
  const w = wildness(x, y);
  if (w >= THICKET) return 2400;
  if (w <= CLEARING) return 350;
  return 1000;
}

/**
 * Weight for choosing a cell to put something on: 1..4, from `crowding`.
 *
 * Used as a repeat count in a weighted draw rather than as a probability, so a
 * pass that places six things puts four of them in the busy third of the zone
 * and still occasionally reaches the quiet part. A hard mask would produce
 * zones with an empty half, which is a different artefact and just as visible.
 */
export function crowdWeight(x: number, y: number): number {
  const c = crowding(x, y);
  return 1 + Math.trunc((c * 3) / NOISE_ONE);
}

/**
 * How far from an open thing - a road, a town door - scenery stays thin.
 *
 * A road that runs through unbroken scrub reads as a tunnel; the classic's roads have
 * trees beside them but the tree line stands off the verge. Two cells, which is
 * one cell of verge either side.
 */
export const OPEN_APRON = 2;
